import { FormView } from '../../UI/view/form-view';
import { FormItemType } from '../../UI/view/component-map';
import { initView } from '../../UI/view/view';
import { getAllLocations } from '../../../services/requests';
import { getOrderDeliveryTypes, getCarriers, getActiveUsers, getAvailableBookings, getContainerOptions, getReturningContainerOptions, getContainerInfo, getCustomerRtuReadings, getOrderBillingInfo, getOrderOperationsInfo, downloadTrackingAttachment, getOrderAttachments, uploadOrderAttachment, downloadOrderAttachment, deleteOrderAttachment, updateOrderAttachmentDescription } from '../../../services/requests/orders/orders-requests';
import { ordersDataSource, ordersPermissions, ordersActionPermissions } from './orders-datasource';
import { OrderType } from './orders-types';
import { formatDate, formatDateTime, formatDateTimeNoTZ } from '../../../services/format';
import { OrderAuditTable } from './orders-audit-list';
import { OrderDewarShipmentsListView } from './orders-dewar-shipments-list';
import { OrderCreditCardStatus } from './orders-credit-card-status';
import { OrderCustomerClipboard, OrderParentLink, OrderCreditCardOverride, OrderClosureNote, OrderDewarInvoiceUploadedNote, OrderPrintProForma } from './orders-info-widgets';
import { OrderActionBar } from './orders-action-bar';
import { orderManifestEditTable } from './orders-manifest-list';
import { OrderCustomerPickerButton } from './orders-customer-picker-widget';
import { OrderContactField } from './orders-contact-field';
import { OrderLocationStatusListView } from './orders-location-status-list';
import {
  GasRecentDeliveriesListView,
  LiquidRecentDeliveriesListView,
  RecentDewarDeliveriesListView,
  DewarContainersOnSiteListView,
  UpcomingOrdersForCustomerListView,
  GasContainersOnSiteListView,
  LiquidContainersOnSiteListView,
  CustomerPurityListView,
} from './orders-current-customer';

// Customer selection reuses the shared customer-list pickers (datagrid + search,
// same component as Misc Billing) — see orders-customer-pickers.ts. Selecting a
// row applies the customer's default field values via applyCustomerDefaultsFor.

// Legacy order-type visibility helpers (SetCustomerInfo / ddlOrderType change):
// certain rows are Liquid-only (Paperwork, HP Container, Forecasted Order) and
// the right-hand context column plus its credit-card controls are Dewar-only.
const isLiquid = ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Liquid;
const isDewar = ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Dewar;
const isGas = ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Gas;
const hideUnlessLiquid = ({ watch }: any) => !isLiquid({ watch });
const hideUnlessDewar = ({ watch }: any) => !isDewar({ watch });
const hideUnlessGas = ({ watch }: any) => !isGas({ watch });
const hideIfDewar = ({ watch }: any) => isDewar({ watch });

// A dropdown value is "unselected" when it is null/undefined, an empty string,
// or the numeric default 0 (numeric-id selects such as Carrier/Booking are
// persisted as 0 when empty). Such values must NOT be surfaced as a literal
// "0" option — the dropdown should just show the blank "Select One" head.
const isBlankSelectValue = (v: any) => v == null || v === '' || String(v) === '0';

// Legacy SetBookingInfo() — the Booking tab renders read-only labels from the
// order's BookingNumber nav (already loaded on the order). These helpers pull a
// nested booking field / formatted booking date out of the form state.
const bk = (getValues: any) => getValues?.('bookingNumber') ?? getValues?.()?.bookingNumber ?? {};
const bkText = (name: string) => (getValues: any) => bk(getValues)?.[name] ?? '';
const bkDate = (name: string) => (getValues: any) => {
  const v = bk(getValues)?.[name];
  return v ? formatDate(v) : '';
};

/**
 * Order Edit form — legacy parity rebuild (Phase 6a).
 *
 * Structure mirrors the legacy `HeliumOrders.aspx` accordion sections; each
 * legacy `<h3>` block becomes one horizontal tab per the user's directive
 * ("collapsible sections will be horizontal tabs"). The left rail keeps the
 * shared info pane + attachments surface used across entity edit pages.
 *
 * Phase 6a scope: **Order Information** tab and **International Dewar
 * Information** tab are fully wired to legacy field bindings (see
 * [HeliumOrders.aspx](../../../../../backend/Orders/Data/Legacy/VTS/Helium/VTS/Logistics/HeliumOrders.aspx)
 * lines 541–1483 and [HeliumOrders.aspx.cs](../../../../../backend/Orders/Data/Legacy/VTS/Helium/VTS/Logistics/HeliumOrders.aspx.cs)
 * lines ~440–520 / ~940–1170). The remaining tabs (Product Manifest, Products
 * Delivered, Current Customer, Shipping Location Status, Container, Booking,
 * RTU, Operations, Billing) are stubs — each shows a placeholder identifying
 * the phase that will implement it. Audit Log and Dewar Shipments already have
 * working custom-component tabs and are wired directly.
 *
 * Field-binding notes (legacy → new):
 * - `tbContactName` → `orderedBy` (legacy sets both `Order.OrderedBy` and
 *   `Order.ContactName` to the same textbox; the backend save handler
 *   mirrors `orderedBy` to `contactName` when the latter is omitted).
 * - `tbContactPhone` → `orderedByPhone`; `tbContactEmail` → `orderedByEmail`;
 *   `tbContactFax` → `orderedByFax`.
 * - International Dewar transport charges: Air → `airFreight`, Land →
 *   `inlandFreight`, Sea → `oceanFreight`, Mode → `transportMode`, Serials →
 *   `dewarList`, ITN → `itn`, ExWorks → `exWorksOverride`.
 * - `dateRequested`, `dateFulfilled`, and `driverArrivalDateTime` use full
 *   date/time inputs; `dateOrdered` remains date-only. The update request
 *   omits legacy time companions so stale loaded values cannot override edits.
 */
export const ordersEdit = {
  pageHeader: 'Order',
  editMode: true,
  permissions: ordersPermissions,
  breadcrumbs: [
    { name: 'Orders', icon: 'bag', to: '/orders' },
  ],
  items: [
    {
      type: FormItemType.masterDetailLayout,
      items: [
        {
          items: [
            {
              type: FormItemType.infoPane,
              header: (state: any) => {
                const o = state.orders?.current?.data;
                if (!o) return '';
                return o.orderNumber ? `Order #${o.orderNumber}` : `Order Id ${o.id}`;
              },
              infoListItems: [
                { name: 'customerName', label: 'Customer Name' },
                { name: 'customerId', label: 'Customer Id' },
                { name: 'locationName', label: 'Location Name' },
                { name: 'statusText', label: 'Status' },
                { name: 'id', label: 'Id' },
                { name: 'orderNumber', label: 'Order #' },
                { name: 'orderOrigin', label: 'Origin' },
                { name: 'bookingId', label: 'Booking Id', value: (entity: any) => entity.bookingId ? entity.bookingId : 'N/A' },

              ],
            },
            // File attachments — the Order page reads/writes the SAME legacy
            // dbo.GlobalAttachments rows the WebForms page uses (ModuleName
            // "Orders", ModuleId = order id), NOT the newer dbo.Attachment store.
            // The reusable LegacyAttachments panel keeps the He3 look while
            // pointing the backend at the legacy attachment system.
            {
              type: FormItemType.legacyAttachments,
              title: 'Attachments',
              moduleIdField: 'id',
              permissions: ordersActionPermissions,
              requests: {
                list: getOrderAttachments,
                upload: uploadOrderAttachment,
                download: downloadOrderAttachment,
                remove: deleteOrderAttachment,
                updateDescription: updateOrderAttachmentDescription,
              },
            },
          ],
        },
        {
          type: FormItemType.tab,
          items: [
            // ================================================================
            // Tab 1 — Order Information (legacy accordion h3 "Order Information")
            // ================================================================
            {
              label: 'Order Information',
              footerItems: [
                // Legacy action bar (HeliumOrders.aspx btnSaveOrder … btnCancel).
                { type: FormItemType.customComponent, component: OrderActionBar, name: 'orderActionBar', permissions: ordersPermissions },
              ],
              items: [
                // Legacy lblClosureNote — shown when the order is Closed/locked.
                {
                  type: FormItemType.customComponent,
                  component: OrderClosureNote,
                  name: 'orderClosureNote',
                },
                // ================================================================
                // Single condensed "Order Information" section. All fields live
                // in one fieldset laid out in a 3-column grid (text areas span
                // full width). The `compact-fields` class shortens field height —
                // it is scoped to this section only (see scss/custom.scss).
                // ================================================================
                {
                  type: FormItemType.fieldset,
                  label: 'Order Information',
                  className: 'compact-fields',
                  items: [
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-columns',
                      balanceColumns: true,
                      balanceColumnNames: [
                        [
                          'orderNumber',
                          'createdOn',
                          'createdByDisplay',
                          'orderTypeId',
                          'customerPicker',
                          'dateRequested',
                          'dateOrdered',
                          'orderedBy',
                          'orderedByPhone',
                          'orderedByEmail',
                          'orderMethod',
                          'ronumber',
                          'carrierUsed',
                          'paperworkPending',
                          'parentOrder',
                          'orderId',
                          'driverArrivalDateTime',
                          'highPressureContainerReq',
                        ],
                        [
                          'deliveryTypeId',
                          'address1',
                          'address2',
                          'city',
                          'state',
                          'zip',
                          'country',
                          'orderedByFax',
                          'shippingLocationId',
                          'ponumber',
                          'paperworkReceivedDate',
                          'childOrders',
                          'pronumber',
                          'driverUserId',
                        ],
                        [
                          'dewarParentAccount',
                          'dewarAccountType',
                          'dewarPoInfo',
                          'dewarBillingStatus',
                          'dewarCreditManager',
                          'dewarTerms',
                          'dateFulfilled',
                          'creditCardPayment',
                          'orderCreditCardStatus',
                          'creditCardOverrideWidget',
                          'dewarCbtNotes',
                        ],
                      ],
                      items: [
                        {
                          name: 'orderTypeId',
                          label: 'Order Type',
                          type: FormItemType.select,
                          searchable: true,
                          required: true,
                          disabled: () => true,
                          options: () => [
                            { id: OrderType.Gas, text: 'Gas' },
                            { id: OrderType.Liquid, text: 'Liquid' },
                            { id: OrderType.Dewar, text: 'Dewar' },
                          ],
                        },
                        {
                          name: 'deliveryTypeId',
                          label: 'Delivery Type',
                          type: FormItemType.select,
                          searchable: true,
                          required: true,
                          // Delivery types are order-type-specific, so this stays
                          // disabled (with a hint) until an Order Type is chosen.
                          disabled: (watch: any) => !Number(watch?.('orderTypeId')),
                          disabledTooltip: 'Please select an Order Type first.',
                          reloadOnFields: ['orderTypeId'],
                          options: async (_data: any, getValues: any) => {
                            // Legacy PopulateDeliveryTypes(): DeliveryTypes.GetAllDeliveryTypes(orderTypeId)
                            // filtered by isGas/isLiquid per order type. The Orders
                            // endpoint mirrors that filter exactly.
                            const orderTypeId = Number(getValues?.('orderTypeId') ?? OrderType.Liquid);
                            const dt = await getOrderDeliveryTypes(orderTypeId);
                            return [
                              { id: '', text: '---Select One---' },
                              ...dt.map((rec: any) => ({ id: rec.id, text: rec.name })),
                            ];
                          },
                        },
                        {
                          name: 'shippingLocationId',
                          label: 'Shipping Location',
                          type: FormItemType.select,
                          searchable: true,
                          required: true,
                          options: async () => {
                            const locations = await getAllLocations();
                            return [
                              { id: '', text: '---Select One---' },
                              ...locations
                                .slice()
                                .sort((a: any, b: any) =>
                                  (a.locationName ?? '').trim().localeCompare((b.locationName ?? '').trim())
                                )
                                .map((l: any) => ({
                                  id: l.locationId,
                                  text: `${l.locationName}`,
                                })),
                            ];
                          },
                        },
                        // Customer picker (opens the customer-list datagrid in a
                        // modal) + clipboard button share one grid column.
                        {
                          type: FormItemType.row,
                          className: 'inline-pair',
                          items: [
                            {
                              type: FormItemType.customComponent,
                              component: OrderCustomerPickerButton,
                              name: 'customerPicker',
                              required: true,
                            },
                            // Legacy ClipBoardButton — copy the selected customer name.
                            {
                              type: FormItemType.customComponent,
                              component: OrderCustomerClipboard,
                              name: 'customerClipboard',
                              fitToContent: true,
                            },
                          ],
                        },
                        // Legacy: lblCreatedOn / lblCreatedBy — read-only header info.
                        { name: 'createdOn', label: 'Created On', type: FormItemType.labelField, computedValue: (getValues) => formatDateTimeNoTZ(getValues('createdOn')) },
                        { name: 'createdByDisplay', label: 'Created By', type: FormItemType.labelField },
                        // Legacy lblOrderNumber — read-only numeric order number
                        // (blank until the order is placed).
                        { name: 'orderNumber', label: 'Order Number', type: FormItemType.labelField },
                        // Legacy lblChildOrders — comma-separated child order numbers.
                        { name: 'childOrders', label: 'Child Orders', type: FormItemType.labelField },
                        {
                          type: FormItemType.row,
                          className: 'inline-pair',
                          items: [
                            {
                              name: 'dateRequested',
                              label: 'Date/Time Requested',
                              type: FormItemType.datetime,
                              required: true,
                            },
                            {
                              name: 'isForecastedOrder',
                              label: 'Forecasted Order',
                              type: FormItemType.checkboxField,
                              defaultValue: false,
                              hide: hideUnlessLiquid,
                              fitToContent: true,
                            },
                          ],
                        },
                        {
                          name: 'dateOrdered',
                          label: 'Date Ordered',
                          type: FormItemType.date,
                          required: true,
                        },
                        {
                          name: 'dateFulfilled',
                          label: 'Date/Time Fulfilled',
                          type: FormItemType.datetime,
                          defaultValue: null,
                          hide: hideUnlessDewar,
                        },
                        {
                          name: 'driverArrivalDateTime',
                          label: 'Driver Arrival Date/Time',
                          type: FormItemType.datetime,
                          defaultValue: null,
                        },
                        {
                          name: 'paperworkReceivedDate',
                          label: 'Paperwork Received Date',
                          type: FormItemType.date,
                          defaultValue: null,
                          hide: hideUnlessLiquid,
                        },
                        {
                          name: 'orderedBy',
                          label: 'Ordered By Contact',
                          type: FormItemType.customComponent,
                          component: OrderContactField,
                          maxLength: 100,
                          defaultValue: '',
                          required: true,
                        },
                        { name: 'orderedByPhone', label: 'Contact Phone #', maxLength: 40, defaultValue: '', required: true },
                        { name: 'orderedByEmail', label: 'Contact Email Address', maxLength: 100, defaultValue: '' },
                        { name: 'orderedByFax', label: 'Contact Fax #', maxLength: 40, defaultValue: '' },
                        { name: 'address1', label: 'Shipping Address 1', maxLength: 200, defaultValue: '' },
                        { name: 'address2', label: 'Shipping Address 2', maxLength: 200, defaultValue: '' },
                        { name: 'city', label: 'City', maxLength: 100, defaultValue: '' },
                        { name: 'state', label: 'State', maxLength: 50, defaultValue: '' },
                        { name: 'zip', label: 'Zip', maxLength: 20, defaultValue: '' },
                        { name: 'country', label: 'Country', maxLength: 50, defaultValue: '' },
                        { name: 'orderId', label: 'Order ID', maxLength: 60, defaultValue: '' },
                        { name: 'ponumber', label: 'PO Number', maxLength: 25, defaultValue: '' },
                        { name: 'ronumber', label: 'RO Number', maxLength: 60, defaultValue: '' },
                        { name: 'pronumber', label: 'PRO Number', maxLength: 25, defaultValue: '' },
                        {
                          name: 'orderMethod',
                          label: 'Order Method',
                          type: FormItemType.select,
                          searchable: true,
                          defaultValue: '',
                          options: () => [
                            { id: '', text: '-- Select One --' },
                            { id: 'Email', text: 'Email' },
                            { id: 'Phone', text: 'Phone' },
                            { id: 'Fax', text: 'Fax' },
                            { id: 'CAS', text: 'CAS' },
                            { id: 'Other', text: 'Other' },
                          ],
                        },
                        // Parent Order + its open-parent link share one grid
                        // column (inline-pair).
                        {
                          type: FormItemType.row,
                          className: 'inline-pair',
                          items: [
                            { name: 'parentOrder', label: 'Parent Order', number: true, defaultValue: null },
                            // Legacy btnParentOrderLink — open the parent order.
                            {
                              type: FormItemType.customComponent,
                              component: OrderParentLink,
                              name: 'parentOrderLink',
                              fitToContent: true,
                            },
                          ],
                        },
                        // Legacy ddlCarrierUsed → Order.CarrierUsed (CarrierID).
                        // The carrier list swaps with order type (Dewar → dewar-only
                        // carriers) exactly like the legacy PopulateCarriers().
                        {
                          name: 'carrierUsed',
                          label: 'Carrier',
                          type: FormItemType.select,
                          searchable: true,
                          defaultValue: null,
                          // Legacy buildHeaderErrorMessage: a Carrier is required
                          // for Gas orders (only).
                          required: ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Gas,
                          zeroIsEmpty: true,
                          reloadOnFields: ['orderTypeId'],
                          options: async (_data: any, getValues: any) => {
                            const orderTypeId = Number(getValues?.('orderTypeId') ?? OrderType.Liquid);
                            const carriers = await getCarriers(orderTypeId);
                            const options = [
                              { id: '', text: '---Select One---' },
                              ...carriers.map((c: any) => ({ id: c.carrierId, text: c.carrierName })),
                            ];
                            // Legacy parity: if the saved carrier is not in the
                            // active list (deleted / opposite dewar flag), add it
                            // so it still selects on load. An empty/0 value means
                            // "no carrier" and must stay blank, not show a "0".
                            const currentId = getValues?.('carrierUsed');
                            if (!isBlankSelectValue(currentId)
                              && !options.some((o) => String(o.id) === String(currentId))) {
                              options.push({ id: currentId, text: String(currentId) });
                            }
                            return options;
                          },
                        },
                        // Legacy ddlDriver → Order.DriverUserID (aspnet username).
                        // Sourced from the application user list (Administration
                        // /Users), filtered to active users, mirroring the legacy
                        // PopulateDrivers(). The legacy username maps to LegacyId.
                        {
                          name: 'driverUserId',
                          label: 'Driver',
                          type: FormItemType.select,
                          searchable: true,
                          defaultValue: '',
                          // Re-run the options once the order entity has loaded so
                          // the saved driver value is present when the list is
                          // built (and thus seeded/selected). Without a reload
                          // trigger the async options resolve before the entity is
                          // hydrated and the value never selects. Mirrors the
                          // Customer/Carrier selects.
                          // driverUserId: the form resets from a previously loaded order, so orderTypeId alone may not change.
                          reloadOnFields: ['orderTypeId', 'driverUserId'],
                          options: async (_data: any, getValues: any) => {
                            const users = await getActiveUsers();
                            const activeUsers = users.filter((u: any) => u.isActive);
                            const currentId = getValues?.('driverUserId');
                            // Legacy stored Order.DriverUserID as a lowered aspnet
                            // LANID in `DOMAIN\username` form (e.g. `linde\e6co05`).
                            // The modern user has no such field — the username lives
                            // as the LOCAL PART of the email / principal name
                            // (e.g. `e6co05@linde.com`). Reduce BOTH the saved value
                            // and each candidate identity field to the bare username
                            // (strip `DOMAIN\` prefix AND `@domain` suffix) so the
                            // legacy value adapts to the new system and the driver's
                            // NAME shows instead of the raw username.
                            const norm = (s: any) => String(s ?? '').toLowerCase().trim();
                            const bareUser = (s: any) => {
                              let v = norm(s);
                              if (v.includes('\\')) v = v.split('\\').pop() ?? '';
                              if (v.includes('@')) v = v.split('@')[0];
                              return v;
                            };
                            const currentUser = bareUser(currentId);
                            const matched = (currentId != null && currentId !== '')
                              ? activeUsers.find((u: any) =>
                                  norm(u.legacyId) === norm(currentId)
                                  || norm(u.activeDirectoryPrincipalName) === norm(currentId)
                                  || norm(u.activeDirectoryMail) === norm(currentId)
                                  || (currentUser !== '' && (
                                      bareUser(u.legacyId) === currentUser
                                      || bareUser(u.activeDirectoryPrincipalName) === currentUser
                                      || bareUser(u.activeDirectoryMail) === currentUser)))
                              : undefined;
                            // The matched user's option keeps the EXACT saved value
                            // as its id so the native <select> selects it on load.
                            const driverValue = (u: any) => (matched && u === matched)
                              ? currentId
                              : (u.legacyId ?? u.activeDirectoryId ?? String(u.userId));
                            const options = [
                              { id: '', text: '---Select One---' },
                              ...activeUsers
                                .map((u: any) => ({ id: driverValue(u), text: u.displayName }))
                                .sort((a: any, b: any) => String(a.text).localeCompare(String(b.text))),
                            ];
                            // Legacy parity: a previously-saved driver whose account
                            // is now inactive/removed (no match above) is still shown
                            // selectable — as the raw value when no name resolves.
                            if (!isBlankSelectValue(currentId)
                              && !options.some((o) => String(o.id) === String(currentId))) {
                              options.push({ id: currentId, text: String(currentId) });
                            }
                            return options;
                          },
                        },
                        // Legacy Flags — Liquid/Dewar only (hidden entirely for Gas via
                        // the per-field order-type hides).
                        { name: 'paperworkPending', label: 'Paperwork Pending', type: FormItemType.checkboxField, defaultValue: false, hide: hideUnlessLiquid },
                        { name: 'highPressureContainerReq', label: 'High Press. Cont. Request', type: FormItemType.checkboxField, defaultValue: false, hide: hideUnlessLiquid },
                        { name: 'creditCardPayment', label: 'Credit Card Payment', type: FormItemType.checkboxField, defaultValue: false, hide: hideUnlessDewar },
                        // Legacy Credit Card Override action (Dewar only, when
                        // Credit Card Payment is selected).
                        {
                          type: FormItemType.customComponent,
                          component: OrderCreditCardOverride,
                          name: 'creditCardOverrideWidget',
                          hide: hideUnlessDewar,
                        },
                        // Dewar Customer Information — read-only labels shown for Dewar
                        // orders (legacy right column). Each field carries the
                        // Dewar-only hide so they stay hidden for Gas/Liquid orders.
                        { name: 'dewarParentAccount', label: 'Parent Account', type: FormItemType.labelField, hide: hideUnlessDewar },
                        { name: 'dewarAccountType', label: 'Account Type', type: FormItemType.labelField, hide: hideUnlessDewar },
                        { name: 'dewarPoInfo', label: 'PO Information', type: FormItemType.labelField, hide: hideUnlessDewar },
                        { name: 'dewarBillingStatus', label: 'Billing Status', type: FormItemType.labelField, hide: hideUnlessDewar },
                        { name: 'dewarCreditManager', label: 'Credit Manager', type: FormItemType.labelField, hide: hideUnlessDewar },
                        { name: 'dewarTerms', label: 'Terms', type: FormItemType.labelField, hide: hideUnlessDewar },
                        { name: 'dewarCbtNotes', label: 'CBT Notes', type: FormItemType.labelField, hide: hideUnlessDewar },
                        // Legacy creditCardStatus — Dewar credit-card status pulled
                        // live from the Billing domain (GetCreditCardStatus()).
                        {
                          type: FormItemType.customComponent,
                          component: OrderCreditCardStatus,
                          name: 'orderCreditCardStatus',
                          hide: hideUnlessDewar,
                        },
                      ],
                    },
                    // Notes — text areas span the full width of the section and
                    // are the LAST fields in the section (nothing follows them).
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-notes',
                      items: [
                        { name: 'deliveryInstructions', label: 'Delivery Instructions', type: FormItemType.textarea, defaultValue: '' },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-notes',
                      items: [
                        { name: 'otherNotes', label: 'Order Notes', type: FormItemType.textarea, defaultValue: '' },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-notes',
                      items: [
                        { name: 'logisticsComments', label: 'Logistics Only Notes', type: FormItemType.textarea, defaultValue: '' },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-notes',
                      items: [
                        {
                          name: 'orderWarnings',
                          label: 'Warnings',
                          type: FormItemType.labelField,
                          style: { color: '#dc3545', fontSize: '1.15rem', fontWeight: 600, whiteSpace: 'pre-line' },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 2 — International Dewar Information (legacy h3)
            // ================================================================
            {
              label: 'International Dewar Info',
              // Legacy `changeIntlDewarsDisplay` (HeliumOrders.aspx.cs ~190): the
              // International Dewar section is shown only for Dewar orders whose
              // customer state is ZZ, PR or GU (US territories / international);
              // hidden otherwise. Evaluated from the loaded order in redux
              // (`orderTypeId` can't change in edit; `dewarCustomerState` is the
              // computed DewarCustomer.State exposed by the backend).
              hide: (state: any) => {
                const o = state?.orders?.current?.data;
                if (!o) return true;
                const dewar = Number(o.orderTypeId) === OrderType.Dewar;
                const st = (o.dewarCustomerState ?? '').trim();
                const intl = st === 'ZZ' || st === 'PR' || st === 'GU';
                return !(dewar && intl);
              },
              footerItems: [
                { type: FormItemType.customComponent, component: OrderActionBar, name: 'orderActionBarIntl', permissions: ordersPermissions },
              ],
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'International Dewar Information Fields',
                  items: [
                    {
                      type: FormItemType.row,
                      items: [
                        { name: 'itn', label: 'ITN #', maxLength: 60, defaultValue: '' },
                        { name: 'dewarList', label: 'Dewar Serial Numbers', maxLength: 500, defaultValue: '' },
                        {
                          name: 'transportMode',
                          label: 'Transportation Mode',
                          type: FormItemType.select,
                          searchable: true,
                          defaultValue: '',
                          options: () => [
                            { id: '', text: '-- Select One --' },
                            { id: 'Air', text: 'Air' },
                            { id: 'Land', text: 'Land' },
                            { id: 'Sea', text: 'Sea' },
                          ],
                        },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      items: [
                        // Legacy tbIntlDewarAirTransportCharge → AirFreight (numeric string).
                        { name: 'airFreight', label: 'Air Transportation Charge', maxLength: 30, defaultValue: '' },
                        // Legacy tbIntlDewarLandTransportCharge → InlandFreight.
                        { name: 'inlandFreight', label: 'Land Transportation Charge', maxLength: 30, defaultValue: '' },
                        // Legacy tbIntlDewarSeaTransportCharge → OceanFreight.
                        { name: 'oceanFreight', label: 'Sea Transportation Charge', maxLength: 30, defaultValue: '' },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      items: [
                        { name: 'exWorksOverride', label: 'ExWorks Override', type: FormItemType.checkboxField, defaultValue: false },
                      ],
                    },
                    {
                      // Legacy btnPrintInternationalProForma + btnPrintFinalInternationalInvoice (SSRS reports).
                      type: FormItemType.customComponent,
                      component: OrderPrintProForma,
                      name: 'orderPrintProForma',
                    },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 3 — Product Manifest (Phase 6b)
            // ================================================================
            {
              label: 'Product Manifest',
              // Legacy divProductManifest is Dewar-only.
              hide: (state: any) => Number(state?.orders?.current?.data?.orderTypeId) !== OrderType.Dewar,
              footerItems: [
                { type: FormItemType.customComponent, component: OrderActionBar, name: 'orderActionBarManifest', permissions: ordersPermissions },
              ],
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'Product Manifest',
                  items: [orderManifestEditTable],
                },
              ],
            },
            // ================================================================
            // Tab 4 — Products Delivered (dbo.OrderDetails; add/edit/delete +
            // auto-reprice, Phase 6c). Dewar-only, mirroring the legacy grid.
            // ================================================================
            {
              label: 'Products Delivered',
              hide: (state: any) => Number(state?.orders?.current?.data?.orderTypeId) !== OrderType.Dewar,
              items: [
                {
                  type: FormItemType.customComponent,
                  component: OrderDewarInvoiceUploadedNote,
                  name: 'orderDewarInvoiceUploadedNote',
                },
                {
                  type: FormItemType.customComponent,
                  component: OrderDewarShipmentsListView,
                  name: 'orderDewarShipmentsList',
                },
              ],
            },
            // ================================================================
            // Tab 5 — Current Customer Information (Phase 6g)
            // ================================================================
            {
              label: 'Current Customer',
              footerItems: [
                { type: FormItemType.customComponent, component: OrderActionBar, name: 'orderActionBarCurrentCustomer', permissions: ordersPermissions },
              ],
              items: [
                // Each section is its own collapsible, labeled fieldset. Only the
                // sections relevant to the order's type render (hidden panels
                // return null, so grids never double-render).
                // --- Gas / Liquid: Recent Deliveries ---
                {
                  type: FormItemType.collapsePanel,
                  label: 'Recent Deliveries',
                  hide: hideUnlessGas,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: GasRecentDeliveriesListView,
                      name: 'orderGasRecentDeliveries',
                    },
                  ],
                },
                {
                  type: FormItemType.collapsePanel,
                  label: 'Recent Deliveries',
                  hide: hideUnlessLiquid,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: LiquidRecentDeliveriesListView,
                      name: 'orderLiquidRecentDeliveries',
                    },
                  ],
                },
                // --- Gas / Liquid: Containers On Site ---
                {
                  type: FormItemType.collapsePanel,
                  label: 'Containers On Site',
                  hide: hideUnlessGas,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: GasContainersOnSiteListView,
                      name: 'orderGasContainersOnSite',
                    },
                  ],
                },
                {
                  type: FormItemType.collapsePanel,
                  label: 'Containers On Site',
                  hide: hideUnlessLiquid,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: LiquidContainersOnSiteListView,
                      name: 'orderLiquidContainersOnSite',
                    },
                  ],
                },
                // --- Gas / Liquid: Customer Purity Requirements ---
                {
                  type: FormItemType.collapsePanel,
                  label: 'Customer Purity Requirements',
                  hide: hideIfDewar,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: CustomerPurityListView,
                      name: 'orderCustomerPurity',
                    },
                  ],
                },
                // --- Dewar: Recent Dewar Deliveries ---
                {
                  type: FormItemType.collapsePanel,
                  label: 'Recent Dewar Deliveries',
                  hide: hideUnlessDewar,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: RecentDewarDeliveriesListView,
                      name: 'orderRecentDewarDeliveries',
                    },
                  ],
                },
                // --- Dewar: Containers On Site ---
                {
                  type: FormItemType.collapsePanel,
                  label: 'Dewar Containers On Site',
                  hide: hideUnlessDewar,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: DewarContainersOnSiteListView,
                      name: 'orderDewarContainersOnSite',
                    },
                  ],
                },
                // --- Dewar: Upcoming Orders (by customer) ---
                {
                  type: FormItemType.collapsePanel,
                  label: 'Upcoming Orders',
                  hide: hideUnlessDewar,
                  items: [
                    {
                      type: FormItemType.customComponent,
                      component: UpcomingOrdersForCustomerListView,
                      name: 'orderUpcomingForCustomer',
                    },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 6 — Shipping Location Status (legacy gvFutureDeliveriesByLocation)
            // ================================================================
            {
              label: 'Shipping Location Status',
              items: [
                {
                  type: FormItemType.customComponent,
                  component: OrderLocationStatusListView,
                  name: 'orderLocationStatus',
                },
              ],
            },
            // ================================================================
            // Tab 7 — Container Information (Phase 6f)
            // ================================================================
            {
              label: 'Container Information',
              // Legacy divContainerInformation is shown for Liquid/Gas (never Dewar).
              hide: (state: any) => Number(state?.orders?.current?.data?.orderTypeId) === OrderType.Dewar,
              footerItems: [
                { type: FormItemType.customComponent, component: OrderActionBar, name: 'orderActionBarContainer', permissions: ordersPermissions },
              ],
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'Container Information',
                  isFluid: true,
                  items: [
                    {
                      type: FormItemType.row,
                      items: [
                        {
                          name: 'containerId',
                          label: 'Container Select',
                          type: FormItemType.select,
                          searchable: true,
                          width: '320px',
                          // 'containerShowAll' is the legacy "All Containers" checkbox
                          // (cbUnfiltered): when checked, load every active container
                          // instead of only those filtered by shipping location.
                          reloadOnFields: ['orderTypeId', 'shippingLocationId', 'containerShowAll'],
                          options: async (_data: any, getValues: any) => {
                            const orderTypeId = Number(getValues?.('orderTypeId'));
                            const shippingLocationId = getValues?.('shippingLocationId');
                            const current = getValues?.('containerId');
                            const showAll = !!getValues?.('containerShowAll');
                            if (!orderTypeId) return [{ id: '', text: '-- Select One --' }];
                            const list = await getContainerOptions(orderTypeId, shippingLocationId ?? null, showAll, current ?? null);
                            return [
                              { id: '', text: '-- Select One --' },
                              ...(list ?? []).map((c: any) => ({ id: c, text: c })),
                            ];
                          },
                        },
                        {
                          name: 'containerShowAll',
                          label: 'All Containers',
                          type: FormItemType.checkboxField,
                          defaultValue: false,
                        },
                        {
                          name: 'returningContainerId',
                          label: 'Returning Container',
                          type: FormItemType.select,
                          searchable: true,
                          width: '320px',
                          // 'returningContainerShowAll' is the legacy "All Containers"
                          // checkbox (cbUnfilteredReturning): when checked, load every
                          // active container instead of only the customer's on-site ones.
                          reloadOnFields: ['orderTypeId', 'customerId', 'returningContainerShowAll'],
                          options: async (_data: any, getValues: any) => {
                            const orderTypeId = Number(getValues?.('orderTypeId'));
                            const customerId = getValues?.('customerId');
                            const showAll = !!getValues?.('returningContainerShowAll');
                            const current = getValues?.('returningContainerId');
                            const head = { id: '', text: '-- Select One --' };
                            if (!orderTypeId || (!customerId && !showAll)) {
                              // Still surface the saved value so it stays selected on load.
                              return !isBlankSelectValue(current) ? [head, { id: current, text: current }] : [head];
                            }
                            const list = await getReturningContainerOptions(orderTypeId, customerId ?? 0, showAll);
                            const opts = [
                              head,
                              ...(list ?? []).map((c: any) => ({ id: c, text: c })),
                            ];
                            // Legacy PopulateContainers: if the saved returning container
                            // isn't in the (filtered) list, add it so it stays selected.
                            if (!isBlankSelectValue(current) && !opts.some((o: any) => String(o.id) === String(current))) {
                              opts.push({ id: current, text: current });
                            }
                            return opts;
                          },
                        },
                        {
                          name: 'returningContainerShowAll',
                          label: 'All Containers',
                          type: FormItemType.checkboxField,
                          defaultValue: false,
                        },
                      ],
                    },
                    {
                      type: FormItemType.asyncDataPanel,
                      name: 'orderContainerInfo',
                      request: getContainerInfo,
                      requestArgs: (watch: any) => [Number(watch?.('orderTypeId')), watch?.('customerId'), watch?.('containerId') ?? null, watch?.('id') ?? 0],
                      watchFields: ['orderTypeId', 'customerId', 'containerId'],
                      sections: [
                        {
                          title: 'Customer Container Requirements',
                          rows: [
                            { headerKey: 'customerHeader1', valueKey: 'customerValue1' },
                            { headerKey: 'customerHeader2', valueKey: 'customerValue2' },
                          ],
                        },
                        {
                          title: 'Container Details',
                          rows: [
                            { headerKey: 'containerHeader1', valueKey: 'containerValue1' },
                            { headerKey: 'containerHeader2', valueKey: 'containerValue2', dangerKey: 'mawpMismatch' },
                            { headerKey: 'containerHeader3', valueKey: 'containerValue3' },
                            { headerKey: 'containerHeader4', valueKey: 'containerValue4', dangerKey: 'statusWarning' },
                          ],
                        },
                      ],
                      chart: {
                        dataKey: 'readings',
                        xKey: 'readingTime',
                        series: [
                          { name: 'He Level', dataKey: 'heLevel' },
                          { name: 'He Pressure', dataKey: 'hePressure' },
                          { name: 'N2 Level', dataKey: 'n2Level' },
                          { name: 'N2 Pressure', dataKey: 'n2Pressure' },
                        ],
                      },
                      footerKeys: ['latestReadings', 'lastLocation'],
                      link: {
                        text: 'View Map',
                        showKey: 'readings',
                        url: (watch: any) => `https://vts.linde.grp/Helium/VTS/Liquid/LiquidTrackingMap.aspx?container=${watch?.('containerId') ?? ''}`,
                      },
                    },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 8 — Booking Information (Phase 6e)
            // ================================================================
            {
              label: 'Booking Information',
              // Legacy divBookingInfo is shown for Liquid/Gas orders (never Dewar).
              hide: (state: any) => Number(state?.orders?.current?.data?.orderTypeId) === OrderType.Dewar,
              footerItems: [
                { type: FormItemType.customComponent, component: OrderActionBar, name: 'orderActionBarBooking', permissions: ordersPermissions },
              ],
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'Booking Information',
                  isFluid: true,
                  items: [
                // --- Booking select + link back into the legacy Bookings UI ---
                // (the booking add/edit screens are not yet ported, so the
                // buttons deep-link into the legacy site).
                {
                  type: FormItemType.row,
                  items: [
                    {
                      name: 'bookingId',
                      label: 'Booking Select',
                      type: FormItemType.select,
                      searchable: true,
                      reloadOnOpen: true,
                      width: '320px',
                      reloadOnFields: ['customerId'],
                      options: async (_data: any, getValues: any) => {
                        const customerId = getValues?.('customerId');
                        const current = getValues?.('bookingId');
                        if (!customerId) return [{ id: '', text: '--Select One--' }];
                        const list = await getAvailableBookings(customerId, current ?? null);
                        return [
                          { id: '', text: '--Select One--' },
                          ...(list ?? []).map((b: any) => ({ id: b.bookingId, text: `${b.bookingId} - ${b.customerName ?? ''}`.trim() })),
                        ];
                      },
                    },
                    {
                      type: FormItemType.button,
                      name: 'addBookingButton',
                      label: 'Add Booking',
                      variant: 'outline-primary',
                      fitToContent: true,
                      style: { marginTop: '1.9rem' },
                      handler: ({ getValues }: any) => {
                        const orderId = getValues?.('id');
                        /*window.open(`/bookings/create?OrderID=${orderId ?? ''}`, '_blank'); */
                        window.open(`https://vts.linde.grp/Helium/VTS/Logistics/Bookings_Add.aspx?orderID=${orderId ?? ''}`, '_blank');
                      },
                    },
                  ],
                },
                // --- Booking ---
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'bookingCustomer', label: 'Customer', type: FormItemType.labelField, computedValue: (getValues: any) => { const b = bk(getValues); const name = getValues('customerName') ?? ''; return b?.customerId ? `${name} - ${b.customerId}` : name; } },
                    { name: 'bookingContainerId', label: 'Container ID', type: FormItemType.labelField, computedValue: bkText('containerId') },
                    { name: 'bookingSourcingPlant', label: 'Sourcing Plant', type: FormItemType.labelField, computedValue: bkText('plantId') },
                    { name: 'bookingPoNumber', label: 'PO Number', type: FormItemType.labelField, computedValue: bkText('ponumber') },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'bookingDateRequested', label: 'Date Requested', type: FormItemType.labelField, computedValue: bkDate('dateRequested') },
                    { name: 'bookingAgent', label: 'Booking Agent', type: FormItemType.labelField, computedValue: bkText('bookingAgent') },
                    { name: 'bookingNumber', label: 'Booking Number', type: FormItemType.labelField, computedValue: bkText('bookNumber') },
                    { name: 'bookingBrokerRef', label: 'Broker Reference Number', type: FormItemType.labelField, computedValue: bkText('brokerRefNumber') },
                  ],
                },
                // --- Booking Link ---
                {
                  type: FormItemType.row,
                  items: [
                    {
                      name: 'bookingLink',
                      label: 'Booking Link',
                      type: FormItemType.labelField,
                      computedValue: (getValues: any) => bk(getValues)?.bookingId ?? '',
                      link: ({ watch }: any) => {
                        const id = watch?.('bookingId');
                        return id ? `https://vts.linde.grp/Helium/VTS/Logistics/Bookings_Edit.aspx?bookingid=${id}` : null;
                      },
                    },
                    { name: 'bookingSupplyPlantPort', label: 'Supply Plant Port', type: FormItemType.labelField, computedValue: bkText('supplyPlantPortName') },
                    { name: 'bookingCustomerPort', label: 'Customer Port', type: FormItemType.labelField, computedValue: bkText('customerPortName') },
                    { name: 'bookingCutoffDate', label: 'Plant Port Cutoff Date', type: FormItemType.labelField, computedValue: (getValues: any) => { const b = bk(getValues); return b?.usportCutoffDate ? `${formatDate(b.usportCutoffDate)} ${b.cutoffTime ?? ''}`.trim() : ''; } },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'bookingSailingDate', label: 'Sailing Date', type: FormItemType.labelField, computedValue: bkDate('sailingDate') },
                    { name: 'bookingCustomerPortEta', label: 'Customer Port ETA', type: FormItemType.labelField, computedValue: bkDate('customerPortEta') },
                    { name: 'bookingVesselName', label: 'Vessel Name', type: FormItemType.labelField, computedValue: bkText('vesselName') },
                    { name: 'bookingVoyageNumber', label: 'Voyage Number', type: FormItemType.labelField, computedValue: bkText('voyageNumber') },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'bookingShipLine', label: 'Ship Line', type: FormItemType.labelField, computedValue: bkText('shipline') },
                    { name: 'bookingLinkSpacer1', label: '', type: FormItemType.labelField, computedValue: () => '' },
                    { name: 'bookingLinkSpacer2', label: '', type: FormItemType.labelField, computedValue: () => '' },
                    { name: 'bookingLinkSpacer3', label: '', type: FormItemType.labelField, computedValue: () => '' },
                  ],
                },
                // --- Rental Tracking Info ---
                { type: FormItemType.sectionHeader, label: 'Rental Tracking Info' },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'bookingPortArrivalDate', label: 'Port Arrival Date', type: FormItemType.labelField, computedValue: bkDate('portReleaseDateOutgoing') },
                    { name: 'bookingCustomsReleaseDate', label: 'Customs Release Date', type: FormItemType.labelField, computedValue: bkDate('portReleaseDateIncoming') },
                    { name: 'bookingReturnPort', label: 'Return Port', type: FormItemType.labelField, computedValue: bkText('returnPortName') },
                    { name: 'bookingReturnPortArrivalDate', label: 'Return Port Arrival Date', type: FormItemType.labelField, computedValue: bkDate('portArrivalReturnDate') },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'bookingReturnCarrier', label: 'Return Carrier', type: FormItemType.labelField, computedValue: bkText('containerPickupCarrier') },
                    { name: 'bookingContainerPickupDate', label: 'Container Pickup Date', type: FormItemType.labelField, computedValue: bkDate('containerPickupDate') },
                    { name: 'bookingTerminalAddress', label: 'Terminal Address', type: FormItemType.labelField, computedValue: bkText('terminalAddress') },
                    { name: 'bookingSpacer1', label: '', type: FormItemType.labelField, computedValue: () => '' },
                  ],
                },
                // --- Cust Arrival Info ---
                { type: FormItemType.sectionHeader, label: 'Cust Arrival Info' },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'arrivalPortArrivalDate', label: 'Port Arrival Date', type: FormItemType.labelField, computedValue: bkDate('customerIncomingArrivalPortDate') },
                    { name: 'arrivalPortPickupDate', label: 'Port Pickup Date', type: FormItemType.labelField, computedValue: bkDate('customerPickUpPortDate') },
                    { name: 'arrivalPlantArrivalDate', label: 'Plant Arrival Date', type: FormItemType.labelField, computedValue: bkDate('customerArrivalPlantDate') },
                    { name: 'arrivalInUseDate', label: 'In-Use Date', type: FormItemType.labelField, computedValue: bkDate('customerInUseAtPlantDate') },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'arrivalHePressure', label: 'Arrival He Pressure', type: FormItemType.labelField, computedValue: (getValues: any) => { const v = bk(getValues)?.arrivalHePressure; return v == null ? '' : Number(v).toFixed(2); } },
                    { name: 'arrivalHeLevel', label: 'Arrival He Level', type: FormItemType.labelField, computedValue: (getValues: any) => { const v = bk(getValues)?.arrivalHeLevel; return v == null ? '' : Number(v).toFixed(2); } },
                    { name: 'arrivalSpacer1', label: '', type: FormItemType.labelField, computedValue: () => '' },
                    { name: 'arrivalSpacer2', label: '', type: FormItemType.labelField, computedValue: () => '' },
                  ],
                },
                // --- Cust Departure Info ---
                { type: FormItemType.sectionHeader, label: 'Cust Departure Info' },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'departureEndUseDate', label: 'End-Use Date', type: FormItemType.labelField, computedValue: bkDate('customerEndUseAtPlantDate') },
                    { name: 'departurePlantDepartureDate', label: 'Plant Departure Date', type: FormItemType.labelField, computedValue: bkDate('customerDeparturePlantDate') },
                    { name: 'departureOutgoingPortDate', label: 'Outgoing Port Date', type: FormItemType.labelField, computedValue: bkDate('customerDeparturePortDate') },
                    { name: 'departurePxReturnEta', label: 'Px Return ETA', type: FormItemType.labelField, computedValue: bkDate('customerUsreturnEta') },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'departureHePressure', label: 'Departure He Pressure', type: FormItemType.labelField, computedValue: (getValues: any) => { const v = bk(getValues)?.departureHePressure; return v == null ? '' : Number(v).toFixed(2); } },
                    { name: 'departureHeLevel', label: 'Departure He Level', type: FormItemType.labelField, computedValue: (getValues: any) => { const v = bk(getValues)?.departureHeLevel; return v == null ? '' : Number(v).toFixed(2); } },
                    { name: 'departureSpacer1', label: '', type: FormItemType.labelField, computedValue: () => '' },
                    { name: 'departureSpacer2', label: '', type: FormItemType.labelField, computedValue: () => '' },
                  ],
                },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 9 — RTU Information (Phase 6i)
            // ================================================================
            {
              label: 'RTU Information',
              // Legacy divRTUInformation is Gas-only (and only when the customer
              // has an RTU — handled by the panel's emptyText).
              hide: (state: any) => Number(state?.orders?.current?.data?.orderTypeId) !== OrderType.Gas,
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'RTU Information',
                  isFluid: true,
                  items: [
                    {
                      type: FormItemType.asyncDataPanel,
                      name: 'orderRtuInfo',
                      request: getCustomerRtuReadings,
                      requestArgs: (watch: any, controls: any) => [watch?.('customerId'), controls?.days ?? 7],
                      watchFields: ['customerId'],
                      emptyText: 'No RTU is configured for this customer.',
                      controls: [
                        { name: 'days', label: 'Chart Readings for last', type: 'number', defaultValue: 7, width: 70, suffix: 'days' },
                      ],
                      chart: {
                        dataKey: 'readings',
                        xKey: 'lastReadingShortDate',
                        height: 320,
                        series: [
                          { name: 'He Pressure', dataKey: 'readingValue' },
                          { name: 'Full Point', dataKey: 'fullPoint' },
                          { name: 'Order Point', dataKey: 'orderPoint' },
                          { name: 'Reserve Point', dataKey: 'reservePoint' },
                          { name: 'Outage Point', dataKey: 'outagePoint' },
                        ],
                      },
                      footerKeys: ['delta'],
                    },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 10 — Operations Information (Phase 6j)
            // ================================================================
            {
              label: 'Operations Information',
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'Operations Information',
                  isFluid: true,
                  items: [
                    {
                      type: FormItemType.asyncDataPanel,
                      name: 'orderOperationsInfo',
                      request: getOrderOperationsInfo,
                      requestArgs: (watch: any) => [watch?.('id')],
                      watchFields: ['id'],
                      dynamicSectionsKey: 'sections',
                      attachments: {
                        key: 'attachments',
                        title: 'Tracking Attachments',
                        download: downloadTrackingAttachment,
                      },
                      emptyText: 'No operations record is available for this order.',
                    },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 11 — Billing Information (Phase 6k)
            // ================================================================
            {
              label: 'Billing Information',
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'Billing Information',
                  isFluid: true,
                  items: [
                    {
                      type: FormItemType.asyncDataPanel,
                      name: 'orderBillingInfo',
                      request: getOrderBillingInfo,
                      requestArgs: (watch: any) => [watch?.('id')],
                      watchFields: ['id'],
                      dynamicSectionsKey: 'sections',
                      emptyText: 'No billing information is available for this order.',
                      // Legacy "Add Gas Delivery Information" — a Gas order that has
                      // shipped but has no gas billing record yet deep-links into the
                      // Gas Billing create-invoice flow, seeded with the order's gas
                      // process tracking number.
                      link: {
                        text: 'Add Gas Delivery Information',
                        showKey: 'canAddGasBilling',
                        newTab: false,
                        url: (watch: any, data: any) =>
                          `/billing/gasproductcreatefromorder/${data?.gasBillingTrackingNumberId}?orderId=${watch('id')}&deliveryTypeId=${watch('deliveryTypeId') ?? ''}`,
                      },
                    },
                  ],
                },
              ],
            },
            // ================================================================
            // Tab 12 — Audit Log (already wired)
            // ================================================================
            {
              label: 'Audit Log',
              unMountOnExit: true,
              items: [
                {
                  type: FormItemType.customComponent,
                  component: OrderAuditTable,
                  name: 'orderAuditList',
                },
              ],
            },
          ],
        },
      ],
    },
  ],
} as FormView;

export const OrdersEditFormView = initView({ form: ordersEdit as any, dataSource: ordersDataSource as any });
